import { cn } from '@heidi/utils';

export const commandClasses = cn(
  'flex size-full flex-col overflow-hidden bg-surface text-text-primary'
);

export const commandDialogContentClasses = cn('max-w-fit overflow-hidden p-0 shadow-lg');

// Applied to the dialog content whenever `banner` is set: a Banner that
// detaches from the command card (positioned absolutely below it) must escape
// the content's own `overflow-hidden` from `sm` up. Paired here so a consumer
// cannot pass the Banner and forget the class.
export const commandDialogBannerClasses = cn('sm:overflow-visible');

// Paired with `commandDialogBannerClasses`: `sm:overflow-visible` stops the
// content clipping the square inner `Command` to the rounded shape, so the
// Command re-supplies the dialog radius and clips itself. `sm:`-only; the mobile
// drawer keeps `overflow-hidden`.
export const commandDialogBannerCommandClasses = cn('sm:rounded-2xl');

export const commandDialogCommandClasses = cn(
  '[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-text-secondary [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-group]]:px-2 [&_[cmdk-input-wrapper]_svg]:size-5 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-3 [&_[cmdk-item]_svg]:size-5'
);

// `border-b` is a divider under the query row, not a field border — the search
// sits at the head of the results list, not in a box of its own.
export const commandInputWrapperClasses = cn(
  'relative inline-flex items-center gap-x-2 border-b px-3'
);

// The leading glyph tints with the field: secondary while empty, easing up to
// primary once there's a query, to signal the field is active. `duration-150`
// matches the control-tier field so the glyph and box cross-fade at one speed.
export const commandInputIconClasses = (active: boolean) =>
  cn(
    'size-4 shrink-0 transition-colors duration-150 ease-out',
    active ? 'text-text-primary' : 'text-text-secondary'
  );

// The stable field uses the `text-*` colour tokens on the `h-9` form-control
// height. Control-tier tokens are gated behind the forms flag, so they live in
// the experimental variant only — flag-off consumers stay on `text-*`.
export const commandInputClasses = cn(
  'flex h-9 w-full rounded-md bg-transparent py-0 text-sm outline-none placeholder:text-text-secondary disabled:cursor-not-allowed disabled:opacity-50'
);

// Only the tint the `X` inherits — the ghost Button wrapping it owns size and cursor.
export const commandInputClearClasses = cn('text-text-secondary');

export const commandListClasses = cn('max-h-[300px] overflow-y-auto overflow-x-hidden');

export const commandEmptyClasses = cn('py-6 text-center text-sm');

export const commandGroupClasses = cn(
  'overflow-hidden p-1 text-text-primary [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-text-secondary'
);

export const commandSeparatorClasses = cn('-mx-1 h-px bg-border');

export const commandItemClasses = cn(
  'relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none data-[disabled=true]:pointer-events-none data-[selected=true]:bg-surface-hover data-[selected=true]:text-text-primary data-[disabled=true]:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0'
);

export const commandShortcutClasses = cn(
  'ms-auto text-xs tracking-widest text-text-secondary'
);
